import { type FC, useCallback, useState } from 'react';

import {
  type AtlasGqlUpdateCommentInput,
  useCreateCommentMutation,
  useGetCommentsQuery,
  useUpdateCommentMutation,
} from 'types/atlas-graphql';

import CommentList, { type CommentListProps } from './CommentList';
import { defaultRefetchConfig } from './refetchConfig';

/**
 * If `maxHeight` (as a string value valid for css max-height) is passed, the
 * container will be restricted to that height and scroll within it. Otherwise,
 * the height will grow as necessary.
 */
interface CommentsProps
  extends Pick<
    CommentListProps,
    'createRule' | 'areWriteActionsVisible' | 'refetchConfig' | 'maxHeight'
  > {
  threadId: string;
  loading?: boolean;
}

const Comments: FC<CommentsProps> = ({
  threadId,
  loading,
  refetchConfig = defaultRefetchConfig,
  ...props
}) => {
  const [editId, setEditId] = useState<string | null>(null);
  const { data, loading: commentsLoading } = useGetCommentsQuery({
    variables: { threadId },
    skip: !threadId,
  });

  const [createComment, { loading: saveLoading }] = useCreateCommentMutation({
    ...refetchConfig,
  });

  const [editComment, { loading: editLoading }] = useUpdateCommentMutation({
    ...refetchConfig,
  });

  const handleSave = useCallback(
    (text: string) => {
      createComment({ variables: { input: { threadId, text } } });
    },
    [threadId, createComment]
  );

  const handleEdit = useCallback(
    ({ id, text }: AtlasGqlUpdateCommentInput) => {
      setEditId(id);
      editComment({ variables: { input: { id, text } } });
    },
    [editComment]
  );

  const comments = data?.comments ?? [];

  return (
    <CommentList
      comments={comments}
      onSave={handleSave}
      onEdit={handleEdit}
      saveLoading={saveLoading}
      editLoading={editLoading}
      editId={editId}
      loading={loading || commentsLoading}
      refetchConfig={refetchConfig}
      {...props}
    />
  );
};

export default Comments;
